// Single source of truth for per-route SEO — Fasol Trade (fasol.tech).
//
// Consumed by BOTH:
//   • runtime  — `useSeo()` (meta/title/canonical/OG on SPA nav) and
//                `<SeoContent>` (visible H1 + copy + FAQ on the landing pages).
//   • build    — `scripts/prerender.mjs` (loads this via Vite SSR and bakes the
//                same data into static dist/<route>.html for crawlers).
//
// Copy is English-only and intentionally independent of the app's i18n (ru/uk)
// — it's marketing/search copy, not UI strings (hreflang is a later phase).
// Titles ≤60, descriptions ≤160.
//
// Positioning (memory `trade-seo-positioning`): connect YOUR OWN AI agent,
// on-chain caller PnL, transparent fees. Non-custodial is framed as a
// CAPABILITY — "you can connect your own non-custodial wallet" — NOT as a
// blanket claim that the platform never holds custody (per Дмитрий: users are
// ALLOWED to use a non-custodial wallet; it's an option, not the sole model).
// So avoid absolutes like "you always hold your keys" / "Fasol never takes
// custody" / "no custody". YMYL discipline — every trading page carries a "not
// financial advice" note (see `DISCLAIMER`); NEVER promise guaranteed returns /
// "X% monthly" / "the bot makes you money" / price predictions. AI agents are
// framed as infrastructure you control, not an autopilot to riches.

export const SITE_URL = 'https://fasol.tech';
export const OG_DEFAULT = `${SITE_URL}/og-default.png`;

/** YMYL risk note rendered on every trading landing (visible + honest E-E-A-T). */
export const DISCLAIMER =
    'Trading memecoins is high-risk and can lose all of your capital. Fasol Trade is software, not financial advice — you control your strategy and your risk, and can connect your own non-custodial wallet.';

// Per-route 1200×630 social cards (public/og-<slug>.png). One brand default for
// now; per-route cards can be added later without touching call-sites.
const OG_IMAGES: Record<string, string> = {
    '/': OG_DEFAULT,
};

export const ogImageFor = (path: string): string => OG_IMAGES[path] ?? OG_DEFAULT;

export interface FaqItem {
    readonly q: string;
    readonly a: string;
}

export interface RouteSeo {
    /** React-router path, also the request path crawlers hit. */
    readonly path: string;
    /** Prerender output file under dist/. Home is '' → overwrites index.html. */
    readonly file: string;
    readonly title: string;
    readonly description: string;
    readonly h1: string;
    /** Keyword body copy, one entry per <p>. */
    readonly intro: readonly string[];
    /** Visible FAQ; also emitted as FAQPage JSON-LD (must match 1:1). */
    readonly faq: readonly FaqItem[];
    /** JSON-LD blocks, each emitted as its own <script type="application/ld+json">. */
    readonly jsonLd: readonly object[];
}

export const canonicalUrl = (path: string): string =>
    path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;

/** Generic per-page SEO contract — resolved tags ready to apply to <head>.
 *  Used by the runtime applier (`applySeo`) and the build-time prerender for
 *  routes that aren't part of the static `ROUTES_SEO` table (e.g. the blog). */
export interface SeoDescriptor {
    readonly path: string;
    readonly title: string;
    readonly description: string;
    /** Absolute canonical URL. */
    readonly canonical: string;
    /** Absolute OG/Twitter image URL. */
    readonly ogImage: string;
    readonly ogType: 'website' | 'article';
    /** OG `article:*` timestamps — article pages only (ISO date). */
    readonly publishedTime?: string;
    readonly modifiedTime?: string;
    /** Emit `<meta name="robots" content="noindex,follow">` (thin/below-gate
     *  dynamic pages, e.g. a low-liquidity coin). Omitted/false → indexable. */
    readonly noindex?: boolean;
    readonly jsonLd: readonly object[];
}

// ─── JSON-LD builders (pure, so visible copy and structured data stay in sync) ──

const organization = (): object => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fasol Trade',
    url: `${SITE_URL}/`,
    logo: OG_DEFAULT,
    sameAs: ['https://x.com/fasol_trade', 'https://t.me/fasol_en'],
});

/** Solana trading terminal is a web app — `WebApplication` is the right type
 *  (FinanceApplication category). Free to use; supports non-custodial wallets. */
const webApplication = (name: string, path: string, description: string): object => ({
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name,
    url: canonicalUrl(path),
    description,
    image: ogImageFor(path),
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Fasol Trade', url: `${SITE_URL}/` },
});

export const faqPage = (faq: readonly FaqItem[]): object => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
});

/** Generic WebPage node — for dynamic pages outside the static ROUTES_SEO
 *  table (e.g. per-coin `/coin/:address`), applied at runtime. */
export const webPage = (name: string, url: string, description: string): object => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    url,
    description,
    isPartOf: { '@type': 'WebSite', name: 'Fasol Trade', url: `${SITE_URL}/` },
});

/** BreadcrumbList from an ordered list of {name, url}. */
export const breadcrumbList = (items: readonly { name: string; url: string }[]): object => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.name,
        item: it.url,
    })),
});

// ─── Per-route FAQ (visible + FAQPage JSON-LD, 1:1) ─────────────────────────────

const HOME_FAQ: readonly FaqItem[] = [
    {
        q: 'Can I use my own non-custodial wallet?',
        a: 'Yes. You can connect your own non-custodial wallet and keep your own keys, trading from a wallet you control.',
    },
    {
        q: 'Can I connect my own AI agent?',
        a: 'Yes. You can point your own AI agent (Claude, Codex or any LLM) at the market and have it execute the strategy and risk limits you define. You stay in control — the agent runs your rules, not a black box.',
    },
    {
        q: 'What can I do on Fasol Trade?',
        a: 'Trade Solana memecoins on a fast terminal, set wallet and price alerts, track smart-money wallets, and follow callers ranked by real on-chain PnL.',
    },
    {
        q: 'What does it cost?',
        a: 'The terminal is free to use with transparent per-trade fees. There is no subscription.',
    },
];

const AGENTS_FAQ: readonly FaqItem[] = [
    {
        q: 'What is bring-your-own-agent trading?',
        a: 'Instead of a built-in black-box bot, you connect your own AI agent (Claude, Codex or any LLM) to the trading API. You write the strategy and the risk limits; your agent reads the market and executes within them. The keys and the rules stay yours.',
    },
    {
        q: 'Which AI agents can I connect?',
        a: 'Any LLM-based agent that can call an API — for example Claude or Codex. Fasol exposes the market data and order actions your agent needs; you supply the prompt, strategy and guardrails.',
    },
    {
        q: 'Is agent trading safe?',
        a: 'Your agent trades under limits you set and you can revoke its access at any time, and you can connect a non-custodial wallet you control. No strategy removes market risk, though — memecoin trading can lose your entire stake, and this is not financial advice.',
    },
    {
        q: 'Does the agent guarantee profits?',
        a: 'No. An AI agent executes your strategy faster and around the clock, but it cannot guarantee returns. Outcomes depend on your strategy and market conditions, and losses are possible.',
    },
];

const CALLERS_FAQ: readonly FaqItem[] = [
    {
        q: 'How are Solana callers ranked?',
        a: 'By real on-chain PnL, not screenshots. Fasol reconstructs each caller’s calls from Solana on-chain data and computes actual win rate and profit, so you can see who really performs.',
    },
    {
        q: 'Can I copy a caller’s trades?',
        a: 'You can follow the callers you trust and act on their calls from the terminal. Timing, slippage and position size are yours to control — copying a call does not remove risk.',
    },
    {
        q: 'Why trust on-chain PnL over a track record post?',
        a: 'Screenshots can be cherry-picked or faked. On-chain PnL is computed from settled Solana transactions, so a caller’s ranked performance reflects what actually happened on-chain.',
    },
];

const ALERTS_FAQ: readonly FaqItem[] = [
    {
        q: 'What alerts can I set on Solana?',
        a: 'Wallet activity, token price levels and smart-money moves. Alerts fire in time so you can act on a wallet you track or a price you care about without watching charts all day.',
    },
    {
        q: 'What are smart-money alerts?',
        a: 'Notifications when wallets with a strong on-chain track record buy or sell. You choose which wallets to watch and get alerted when they move.',
    },
    {
        q: 'Do alerts work in Telegram?',
        a: 'Yes. Alerts can reach you where you already are, including Telegram, so you never miss a move you set up to catch.',
    },
];

// ─── Per-route content ────────────────────────────────────────────────────────

export const ROUTES_SEO: readonly RouteSeo[] = [
    {
        path: '/',
        file: '',
        title: 'Fasol Trade — Solana Memecoin Terminal with AI-Agent Trading',
        description:
            'Trade Solana memecoins on a fast terminal. Connect your own AI agent and a non-custodial wallet, set smart alerts, and follow callers ranked by on-chain PnL.',
        h1: 'Trade Solana memecoins — with your own AI agent',
        intro: [
            'Fasol Trade is a Solana memecoin terminal. Trade fast, set wallet and price alerts, track smart-money, and follow callers ranked by real on-chain PnL — and connect your own non-custodial wallet to trade from keys you control.',
            'What makes Fasol different is agent-native trading: connect your own AI agent — Claude, Codex or any LLM — and have it execute the strategy and risk limits you define. You write the rules; your agent runs them. No black-box bot, transparent fees.',
        ],
        faq: HOME_FAQ,
        jsonLd: [
            organization(),
            webApplication(
                'Fasol Trade',
                '/',
                'Solana memecoin terminal with bring-your-own AI-agent trading, non-custodial wallet support, alerts and on-chain caller rankings.',
            ),
            faqPage(HOME_FAQ),
        ],
    },
    {
        path: '/agents',
        file: 'agents.html',
        title: 'AI-Agent Trading on Solana — Connect Claude or Codex | Fasol Trade',
        description:
            'Point your own AI agent (Claude, Codex, any LLM) at Solana memecoin markets. You write the strategy and risk limits — your agent executes on your terms.',
        h1: 'Bring your own AI agent to Solana trading',
        intro: [
            'Fasol Trade is agent-native: instead of a built-in black-box bot, you connect your own AI agent — Claude, Codex or any LLM — to the Solana trading API. You write the strategy and the risk limits, and your agent reads the market and executes within them.',
            'Your agent trades under rules you set and you can revoke its access at any time, and you can connect your own non-custodial wallet to trade from keys you control. This is infrastructure you control — not an autopilot that promises profits. Memecoin trading is high-risk, and no agent removes that.',
        ],
        faq: AGENTS_FAQ,
        jsonLd: [
            webApplication(
                'Fasol Trade — AI-Agent Trading',
                '/agents',
                'Connect your own AI agent (Claude, Codex or any LLM) to a Solana trading API, with non-custodial wallet support. You define the strategy and risk; your agent executes.',
            ),
            faqPage(AGENTS_FAQ),
        ],
    },
    {
        path: '/callers',
        file: 'callers.html',
        title: 'Solana Callers & KOL Calls — Ranked by On-Chain PnL | Fasol Trade',
        description:
            'See which Solana callers actually perform — ranked by real on-chain PnL, not screenshots. Track calls, check win rates, trade the ones you trust.',
        h1: 'Solana callers, ranked by on-chain performance',
        intro: [
            'Most caller "track records" are screenshots. Fasol ranks Solana callers and KOLs by real on-chain PnL — reconstructed from settled Solana transactions — so you can see actual win rates and profit instead of cherry-picked wins.',
            'Follow the callers you trust and act on their calls from the terminal, with timing, slippage and position size under your control. Seeing who really performs is an edge; it is not a guarantee, and copying a call does not remove risk.',
        ],
        faq: CALLERS_FAQ,
        jsonLd: [
            webApplication(
                'Fasol Trade — Callers',
                '/callers',
                'Solana callers and KOLs ranked by real on-chain PnL, not screenshots. Track calls and win rates from on-chain data.',
            ),
            faqPage(CALLERS_FAQ),
        ],
    },
    {
        path: '/alerts',
        file: 'alerts.html',
        title: 'Solana Wallet & Price Alerts — Smart-Money Tracking | Fasol Trade',
        description:
            'Set Solana wallet, price and smart-money alerts that fire in time. Track winning wallets, watch price levels, and never miss a move — including in Telegram.',
        h1: 'Solana alerts that actually fire in time',
        intro: [
            'Set alerts for wallet activity, token price levels and smart-money moves on Solana. Track the wallets that matter, watch the prices you care about, and get notified in time to act — including in Telegram.',
            'Smart-money alerts let you follow wallets with a strong on-chain track record and hear about it when they buy or sell. You choose what to watch; the alerts do the watching.',
        ],
        faq: ALERTS_FAQ,
        jsonLd: [
            webApplication(
                'Fasol Trade — Alerts',
                '/alerts',
                'Solana wallet, price and smart-money alerts that fire in time, delivered where you are including Telegram.',
            ),
            faqPage(ALERTS_FAQ),
        ],
    },
    {
        path: '/market-overview',
        file: 'market-overview.html',
        title: 'Solana Market Overview — Trending Tokens & Smart Money | Fasol Trade',
        description:
            'A live overview of trending Solana tokens, volume and smart-money flow. Spot what is moving and where on-chain capital is going.',
        h1: 'Solana market overview',
        intro: [
            'A live overview of the Solana memecoin market — trending tokens, volume and smart-money flow in one place. See what is moving and where on-chain capital is rotating before you open a position.',
            'Everything is drawn from on-chain Solana data. It is market information, not a recommendation — you decide what, if anything, to trade.',
        ],
        faq: [],
        jsonLd: [
            webApplication(
                'Fasol Trade — Market Overview',
                '/market-overview',
                'Live overview of trending Solana tokens, volume and smart-money flow from on-chain data.',
            ),
        ],
    },
    {
        path: '/tracker',
        file: 'tracker.html',
        title: 'Solana Wallet & Token Tracker | Fasol Trade',
        description:
            'Track Solana wallets and tokens in real time — positions, flows and the moves that matter. Real-time on-chain data.',
        h1: 'Solana wallet & token tracker',
        intro: [
            'Track Solana wallets and tokens in real time — positions, inflows and outflows, and the moves that matter. Watch the wallets you care about and see what they are doing on-chain as it happens.',
            'The tracker reads public Solana on-chain data. It shows you what is happening; the trading decisions stay yours.',
        ],
        faq: [],
        jsonLd: [
            webApplication(
                'Fasol Trade — Tracker',
                '/tracker',
                'Real-time Solana wallet and token tracker — positions and flows from on-chain data.',
            ),
        ],
    },
];

export const getRouteSeo = (pathname: string): RouteSeo | undefined =>
    ROUTES_SEO.find((r) => r.path === pathname);

/** Head-only routes: correct <title>/canonical/OG for crawlers, but NO visible
 *  SEO copy (the page renders its own content). Baked with an empty
 *  #seo-fallback so the real page hydrates without a flash. Low sitemap
 *  priority. Used for legal/boilerplate pages. */
export interface MetaOnlyRoute {
    readonly path: string;
    readonly file: string;
    readonly title: string;
    readonly description: string;
    /** Baked into the static HTML for page structure (crawler first-wave). The
     *  real page renders its own content once JS boots. */
    readonly h1: string;
}

export const META_ONLY_SEO: readonly MetaOnlyRoute[] = [
    {
        path: '/privacy',
        file: 'privacy.html',
        title: 'Privacy Policy | Fasol Trade',
        description: 'How Fasol Trade handles data on its Solana trading terminal.',
        h1: 'Privacy Policy',
    },
    {
        path: '/terms',
        file: 'terms.html',
        title: 'Terms of Use | Fasol Trade',
        description: 'The terms that govern use of the Fasol Trade Solana terminal.',
        h1: 'Terms of Use',
    },
];
